import type { EventLogFromSocket, EventsLogResultData } from 'src/types/requests/eventslog/events_log';

import { normalizeEventLogEntry } from 'src/components/EventLog/event_log_v2_normalizer';
import {
	isEventLogV2Enabled,
	matchesEventLogChannel,
	parseEventLogPayload,
} from 'src/components/EventLog/event_log_version_helpers';

interface EventLogStructure {
	result?: Record<string, EventLogFromSocket[]>;
}

/**
 * Camera events — 22 (motion) and 24 (arm / privacy) — carry a value at payload
 * index 1 instead of a channel.
 */
const CAMERA_CHANNEL_LESS_EVENTS = new Set([22, 24]);

class EventLogStore {
	private _deviceId: string | number | null = null;
	private _channel: string | number | null = null;
	private _events: EventLogStructure = {};
	private _subscribers: ((events: EventLogStructure) => void)[] = [];

	get channel(): string | number | null {
		return this._channel;
	}

	set channel(channel: string) {
		this._channel = channel;
	}

	get deviceId(): string | number | null {
		return this._deviceId;
	}

	set deviceId(id: string) {
		this._deviceId = id.replace(/_.*/, '');
	}

	get events(): EventLogStructure {
		return this._events;
	}

	clear(): void {
		this._deviceId = null;
		this._events = {};
		this.notifySubscribers();
	}

	addEvent(event: EventLogFromSocket): void {
		const entry: EventsLogResultData = {
			t: Number(event.t),
			e: event.e,
			p: event.p as EventsLogResultData['p'],
			tag: String(this._deviceId),
			rowid: 0,
		};

		const normalizedEntry = isEventLogV2Enabled() ? normalizeEventLogEntry(entry) : entry;
		const parsedPayload = parseEventLogPayload(normalizedEntry.p);

		if (!Array.isArray(parsedPayload) || this._deviceId != parsedPayload[0]) {
			return;
		}

		if (
			!CAMERA_CHANNEL_LESS_EVENTS.has(event.e) &&
			!matchesEventLogChannel(this._channel, parsedPayload)
		) {
			return;
		}

		const reformeddEvent = {
			...event,
			t: event.t,
			p:
				typeof normalizedEntry.p === 'string'
					? normalizedEntry.p
					: JSON.stringify(normalizedEntry.p),
			tag: this._deviceId,
		};

		this._events.result = this._events.result || {};
		this._events.result[this._deviceId] = [
			...(this._events.result[this._deviceId] || []),
			reformeddEvent,
		];
		this.notifySubscribers();
	}

	subscribe(callback: (events: EventLogStructure) => void): () => void {
		if (!this._subscribers) {
			this._subscribers = [];
		}
		this._subscribers.push(callback);
		callback(this._events);
		return () => {
			this._subscribers = this._subscribers.filter((sub) => sub !== callback);
		};
	}

	private notifySubscribers(): void {
		for (const sub of this._subscribers) {
			sub(this._events);
		}
	}

	getAllEvents(callback: (events: EventLogStructure) => void): () => void {
		return this.subscribe(callback);
	}
}

export const eventLogStore = new EventLogStore();
